import React, { FC, useState } from "react";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import SearchInput from "../../../components/form/SearchInput";
import FilterByButton from "../../ui/FilterButton";
import DateRangeSelector from "../../ui/DateRangeSelector";
import { COUPON_TYPE_OPTIONS } from "./schema";
import {
  CouponDiscountType,
  CouponStatus,
  CouponType,
  COUPON_DISCOUNT_TYPE_OPTIONS,
  COUPON_STATUS_OPTIONS,
} from "../../../redux/features/types/couponListTypes";
import { UseCouponFiltersReturn } from "./useCouponFilters";

const prettifyCouponType = (raw: string): string =>
  raw
    ?.split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ") ?? "";

const couponTypeFilterOptions = [
  { label: "All", value: null },
  ...COUPON_TYPE_OPTIONS.map((t) => ({ label: prettifyCouponType(t), value: t })),
];

export type CouponFilterKey =
  | "couponType"
  | "status"
  | "discountType"
  | "search"
  | "buyerName"
  | "createdRange"
  | "expiryRange";

const ALL_FILTER_KEYS: CouponFilterKey[] = [
  "couponType",
  "status",
  "discountType",
  "search",
  "buyerName",
  "createdRange",
  "expiryRange",
];

const FilterField: FC<{
  label: string;
  active?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({ label, active = false, children, className = "" }) => (
  <div className={`flex flex-col gap-1 w-full ${className}`}>
    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
      {label}
    </label>
    <div className={`rounded-lg ${active ? "ring-2 ring-primary/40" : ""}`}>
      {children}
    </div>
  </div>
);

interface CouponFilterBarProps {
  filters: UseCouponFiltersReturn;
  /** Optional id for aria-controls on the toggle. */
  panelId?: string;
  /**
   * Subset of filter keys to show. Defaults to all 7. The active-filter
   * badge and Clear button scope to the visible subset, so each tab manages
   * only the filters it actually surfaces — filters set on a different tab
   * stay quietly persistent in the URL.
   */
  visibleFilters?: CouponFilterKey[];
}

/**
 * Shared filter chrome for the All Coupons / Used Coupons / Cashback tables.
 * Purely presentational — all state lives in `useCouponFilters`. Categorical
 * and date controls are remounted on Clear via `filterResetKey` so the
 * displayed label / range snaps back to the default.
 */
const CouponFilterBar: FC<CouponFilterBarProps> = ({
  filters,
  panelId = "coupons-filter-panel",
  visibleFilters = ALL_FILTER_KEYS,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 640px)").matches
  );

  const {
    inputValue,
    setInputValue,
    buyerNameInput,
    setBuyerNameInput,
    search,
    buyerName,
    couponType,
    setCouponType,
    status,
    setStatus,
    discountType,
    setDiscountType,
    createdRange,
    setCreatedRange,
    expiryRange,
    setExpiryRange,
    filterResetKey,
  } = filters;

  const isCreatedRangeActive = !!(createdRange.startDate || createdRange.endDate);
  const isExpiryRangeActive = !!(expiryRange.startDate || expiryRange.endDate);

  const visible = new Set(visibleFilters);
  const showCouponType = visible.has("couponType");
  const showStatus = visible.has("status");
  const showDiscountType = visible.has("discountType");
  const showSearch = visible.has("search");
  const showBuyerName = visible.has("buyerName");
  const showCreatedRange = visible.has("createdRange");
  const showExpiryRange = visible.has("expiryRange");

  // Scoped active count — only counts filters surfaced on this tab. A filter
  // inherited from a different tab via the URL won't ring this tab's badge.
  const scopedActiveCount =
    (showSearch && search ? 1 : 0) +
    (showBuyerName && buyerName ? 1 : 0) +
    (showCouponType && couponType ? 1 : 0) +
    (showStatus && status ? 1 : 0) +
    (showDiscountType && discountType ? 1 : 0) +
    (showCreatedRange && isCreatedRangeActive ? 1 : 0) +
    (showExpiryRange && isExpiryRangeActive ? 1 : 0);
  const scopedHasActiveFilters = scopedActiveCount > 0;

  // Scoped clear — only resets the filters surfaced on this tab. Filters
  // inherited from a different tab keep their values in the URL.
  const handleScopedClear = () => {
    if (showSearch) {
      setInputValue("");
    }
    if (showBuyerName) {
      setBuyerNameInput("");
    }
    if (showCouponType) setCouponType(null);
    if (showStatus) setStatus(null);
    if (showDiscountType) setDiscountType(null);
    if (showCreatedRange) setCreatedRange({ startDate: null, endDate: null });
    if (showExpiryRange) setExpiryRange({ startDate: null, endDate: null });
    filters.bumpFilterResetKey();
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          aria-expanded={filtersOpen}
          aria-controls={panelId}
        >
          <SlidersHorizontal className="w-4 h-4 text-gray-500" />
          <span>Filters</span>
          {scopedHasActiveFilters && (
            <span className="bg-lightPurple text-accent text-[10px] font-semibold px-2 py-0.5 rounded-full">
              {scopedActiveCount}
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
              filtersOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {scopedHasActiveFilters && (
          <button
            type="button"
            onClick={handleScopedClear}
            className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 hover:underline"
          >
            <X className="w-3.5 h-3.5" />
            Clear filters
          </button>
        )}
      </div>

      {/* Collapsible body — kept mounted (visibility toggled) so dropdown
          selections survive collapse/expand. Single flex-wrap row with
          fixed, content-appropriate widths so controls sit at a sensible
          size and only wrap when they run out of horizontal room. */}
      <div id={panelId} className={`mt-4 ${filtersOpen ? "" : "hidden"}`}>
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          {showCouponType && (
            <FilterField
              label="Coupon type"
              active={!!couponType}
              className="w-full sm:w-[220px]"
            >
              <FilterByButton
                key={`type-${filterResetKey}`}
                fullWidth
                defaultValue={couponType}
                buttonLabel="All types"
                options={couponTypeFilterOptions}
                onSelect={(value: CouponType | null) => setCouponType(value)}
              />
            </FilterField>
          )}

          {showStatus && (
            <FilterField
              label="Status"
              active={!!status}
              className="w-full sm:w-[220px]"
            >
              <FilterByButton
                key={`status-${filterResetKey}`}
                fullWidth
                defaultValue={status}
                buttonLabel="All statuses"
                options={COUPON_STATUS_OPTIONS}
                onSelect={(value: CouponStatus | null) => setStatus(value)}
              />
            </FilterField>
          )}

          {showDiscountType && (
            <FilterField
              label="Discount type"
              active={!!discountType}
              className="w-full sm:w-[220px]"
            >
              <FilterByButton
                key={`discount-${filterResetKey}`}
                fullWidth
                defaultValue={discountType}
                buttonLabel="All discount types"
                options={COUPON_DISCOUNT_TYPE_OPTIONS}
                onSelect={(value: CouponDiscountType | null) =>
                  setDiscountType(value)
                }
              />
            </FilterField>
          )}

          {showSearch && (
            <FilterField
              label="Search"
              active={!!search}
              className="w-full sm:w-[260px]"
            >
              <SearchInput
                wrapperClassName=""
                placeholder="Code, buyer, or email"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </FilterField>
          )}

          {showBuyerName && (
            <FilterField
              label="Buyer name"
              active={!!buyerName}
              className="w-full sm:w-[260px]"
            >
              <SearchInput
                wrapperClassName=""
                placeholder="Filter by buyer"
                value={buyerNameInput}
                onChange={(e) => setBuyerNameInput(e.target.value)}
              />
            </FilterField>
          )}

          {showCreatedRange && (
            <FilterField
              label="Created date"
              active={isCreatedRangeActive}
              className="w-full sm:w-[260px]"
            >
              <DateRangeSelector
                key={`created-${filterResetKey}`}
                disableFuture
                className="w-full"
                placeholder="Created from – to"
                initialRange={createdRange}
                onChange={(range) => setCreatedRange(range)}
              />
            </FilterField>
          )}

          {showExpiryRange && (
            <FilterField
              label="Expiry date"
              active={isExpiryRangeActive}
              className="w-full sm:w-[260px]"
            >
              <DateRangeSelector
                key={`expiry-${filterResetKey}`}
                className="w-full"
                placeholder="Expiry from – to"
                initialRange={expiryRange}
                onChange={(range) => setExpiryRange(range)}
              />
            </FilterField>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponFilterBar;
export { prettifyCouponType };
